import React, {useEffect, useRef, useState} from 'react';
import { connect } from 'react-redux';
import { withStyles, Tooltip, IconButton, Typography, Dialog } from '@material-ui/core';
import { FindInPage, Info, Send } from '@material-ui/icons';
import { Redirect } from 'react-router-dom';
import {hasPermission, createHistory, hasAtLeastOnePermission, parseURL, formatDate} from '../../../sc-utilities';
import UserList from './UserList';
import * as actions from './store/userActions';
import { informationColors } from '../../../../themes';
import * as groupActions from "../../../sc-group/Group/store/groupActions";
import AppLogoLoading from "../../../sc-logo";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    loading: {
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "hidden",
        alignItems: "center"
    },
});

export const checkIfLoading = (loading) => {
    return !!(Object.keys(loading).find((key) => loading[key]===true));
}

const UserAdminSettings = props => {
    const { classes, columns, translations, permissions, activeCustomerID, sendCredentials, fetchGroups } = props;
    const [data, setData] = useState({ name: '', data: [], open: false });

    let searchParams = parseURL(window.location.search);
    const [groups, setGroups] = useState(null);
    const [loading, setLoading] = useState({groups: false});

    const tableRef = useRef();
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, userID: null, redirectPath: '' });
    const redirectHandler = (path, userID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, userID: userID, redirectPath: path })
    };

    useEffect(() => {
        const getGroups = async () => {
            setLoading(prevState => {return {...prevState, groups: true}});
            const resultGroups = await fetchGroups({ ActiveCustomerID: activeCustomerID});
            if (resultGroups) {
                setGroups(resultGroups.data);
            }
            setLoading(prevState => {return {...prevState, groups: false}});
        }

        if (hasPermission(permissions, 'group_list')) {
            getGroups();
        }
    }, [fetchGroups, activeCustomerID, permissions]);

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    const reSendCredentials = async event => {
        const userID = event.currentTarget.value;
        await sendCredentials({ UserID: userID, ActiveCustomerID: activeCustomerID });
    }

    const getLastUserActivity = (rowData) => {
        let client = "";
        let dateTime = "";
        if (rowData?.UserSession) {
            const lastSession = rowData.UserSession;
            if (lastSession.LastActivity) {
                dateTime = formatDate(lastSession.LastActivity);
            }
            if (lastSession.ClientType && lastSession.ClientVersion) {
                client = `${lastSession.ClientType} ${lastSession.ClientVersion}`;
            }
        } else {
            return translations['short_not_assigned'] || 'n/a';
        }
        return <span>{client}<br/>{dateTime}</span>;
    }

    let visible = columns && columns['User'];
    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 150, maxWidth: 150 },
            filtering: false,
            render: rowData => <>
                {
                    hasPermission(permissions, 'user_load') ? <Tooltip title={translations["user_details"] || "User details"}>
                        <IconButton
                            id={`edit_user_${rowData.UserID}`}
                            component='div'
                            onClick={(event) => redirectHandler(`/users/details?userID=${rowData.UserID}`)}
                        >
                            <FindInPage />
                        </IconButton>
                    </Tooltip>
                        : null
                }
                {
                    hasPermission(permissions, 'access_all_customers') ?
                        <>
                            <Tooltip title={translations['re_send_credentials'] || "Re-send credentials"}>
                                <IconButton value={rowData.UserID} aria-label={translations['re_send_credentials'] || "Re-send credentials"} onClick={reSendCredentials}>
                                    <Send />
                                </IconButton>
                            </Tooltip></>
                        : null
                }
            </>
        },
        {
            title: translations["first_name"] || 'First name', field: 'FirstName',
            hidden: visible ? visible['FirstName'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.FirstName ? rowData.FirstName : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["last_name"] || 'Last name', field: 'LastName',
            hidden: visible ? visible['LastName'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.LastName ? rowData.LastName : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["username"] || 'Username', field: 'Username',
            hidden: visible ? visible['Username'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Username ? rowData.Username : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["e_mail"] || 'Email', field: 'Email',
            hidden: visible ? visible['Email'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Email ? rowData.Email : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["status"] || 'Status', field: 'Status',
            hidden: visible ? visible['Status'] : false,
            filtering: true,
            lookup: { '1': translations['active'] || 'Active', '0': translations['inactive'] || 'Inactive' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <>
                    <CustomChip
                        className={ rowData.Status === 1 ? 'active' : 'inactive' }
                        key={'user_status'}
                        value={ rowData.Status === 1 ? translations['active'] || 'Active' : translations['inactive'] || 'Inactive' }
                    />
                </>
            }
        },
        {
            title: translations["deleted"] || 'Deleted', field: 'Deleted',
            hidden: visible ? visible['Deleted'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0'],
            render: rowData => {
                return <Typography variant={'caption'}>{ rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No' }</Typography>;
            }
        },
        {
            title: translations["last_activity"] || 'Last activity', field: 'LastActivity',
            hidden: visible ? visible['LastActivity'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{getLastUserActivity(rowData)}</Typography>;
            }
        },
    ];

    if (hasPermission(permissions, 'access_all_customers')) {
        tableColumns.splice(1, 0, {
            title: translations["customer_role"] || 'Customer (role)', field: 'Customers',
            hidden: visible ? visible['Customer'] : false,
            filtering: true,
            render: rowData =><Typography variant={'caption'}>{rowData.Customers ? rowData.Customers : translations['short_not_assigned'] || 'n/a'}</Typography>},)
    }

    tableColumns = hasPermission(permissions, 'group_list')? tableColumns : tableColumns.filter((column) => column.field!=="GroupID");

    if (!hasAtLeastOnePermission(permissions, ['user_load'])) {
        tableColumns.shift();
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'user_list') ?
                !checkIfLoading(loading)? <UserList
                    groups={groups}
                    tableColumns={tableColumns}
                    tableRef={tableRef}
                    redirectHandler={redirectHandler}
                /> :    <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={checkIfLoading(loading)} PaperProps={{classes: {root: classes.loading}}}>
                            <AppLogoLoading />
                        </Dialog>
                :
                null}
            <Dialog
                open={data.open}
                disableBackdropClick={false}
                disableEscapeKeyDown={false}
                disableEnforceFocus={false}
                fullWidth={true}
                onClose={() => setData({ ...data, open: false })}
            >
                <div style={{ marginLeft: '12px', marginTop: '6px' }}><h2>{translations['role_information_for'] || 'Role information for'} {data.name} </h2></div>
                <div style={{ minHeight: ' 100px', minWidth: '100px', float: 'center', margin: '15px' }}>{data.data.map(role => <Typography key={role}> {role}<br/></Typography>)}</div>
            </Dialog>
        </div>
    );
};

const CustomChip = props => {
    const { keyValue, value, className } = props;
    let possibleStyles = {
        inactive: {
            color: informationColors.error.dark,
        },
        active: {
            color: informationColors.success.main
        }
    }
    return (
        <Typography
            variant={'caption'}
            key={keyValue}
            className={className}
            style={possibleStyles[className]}>{value === 'n/a' ? value : capitalize(value)}</Typography>
    );
};

const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
}
const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state.core.login.activeCustomer.CustomerID
    };
};

const mapDispatchToProps = dispatch => {
    return {
        sendCredentials: (data) => dispatch(actions.sendCredentials(data)),
        fetchGroups: params => dispatch(groupActions.fetchGroups(params)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(UserAdminSettings));